import moment from 'moment-timezone'
import { toast } from 'react-toastify'
import { SubmissionError } from 'redux-form'
import { SagaIterator } from 'redux-saga'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import {
  acceptApplication,
  acceptApplicationFail,
  acceptApplicationSuccess,
  editApplication,
  editApplicationFail,
  editApplicationSuccess,
  getApartmentApplications,
  getApartmentApplicationsFail,
  getApartmentApplicationsSuccess,
  getApplication,
  getApplicationFail,
  getApplicationSuccess,
  listApplications,
  listApplicationsFail,
  listApplicationsSuccess,
  rejectApplication,
  rejectApplicationFail,
  rejectApplicationSuccess,
  updateApplicationAdminNotes,
  updateApplicationAdminNotesFail,
  updateApplicationAdminNotesSuccess,
  updateApplicationSuccess,
  selectApplications,
  selectApplicationsSuccess,
  selectApplicationsFail,
  updateApplicationStatusByCode,
  updateApplicationStatusByCodeFail,
  updateApplicationStatusByCodeSuccess,
  updateApplicationStatus,
  updateApplicationStatusFail,
  updateApplicationStatusSuccess,
  createApplicationFail,
  createApplication,
  createApplicationSuccess,
  updateApplicationPhoneShownByCodeSuccess,
  updateApplicationPhoneShownByCode,
  getApartmentLeadSuccess,
  getApartmentLeadFail,
  getApartmentLead,
  getConversationSuccess,
  getConversationFail,
  getConversation
} from '../actions/applications'
import { closeDialog } from '../actions/dialogs'
import { navigate } from '../actions/routes'
import { UpdateStatusByCodeFormData } from '../containers/Application/Forms/UpdateApplicationStatusByCodeForm'
import { Action } from '../reducers/types'
import { selectSelectedApartmentId } from '../selectors/apartments'
import { selectSelectedApplications } from '../selectors/applications'
import {
  selectContentIdFromPathname,
  selectUserIdFromPathname,
  selectApplicationCodeFromPathname
} from '../selectors/router'
import { selectReserveData } from '../selectors/showings'
import ApplicantAvailabilityApi from '../services/applicantAvailability'
import Api from '../services/applications'
import ShowingsApi from '../services/showings'
import { getHistory } from '../store'
import i18n from '../translations/i18n'
import { AddApplicantAvailabilityFormData } from '../types/applicantAvailability'
import {
  Application,
  ApplicationData,
  CreateApplicationData,
  CensoredApplication,
  PartnerLead
} from '../types/applications'
import { FormPayloadParameters } from '../types/formData'
import { ReserveData, Showing } from '../types/showings'
import { validateIsAdmin } from '../utils/validation/users'
import {
  uploadFileToS3
} from '../services/storage/s3'
import config from '../config/config'

export function* handleGetApplication({
  payload: { apartmentId, applicationId }
}: Action): SagaIterator {
  try {
    const application: Application = yield call(
      Api.getApplication,
      apartmentId,
      applicationId
    )
    yield put(getApplicationSuccess(application))
  } catch (error) {
    yield put(getApplicationFail(error.message))
  }
}

export function* handleListApplications(): SagaIterator {
  try {
    const applications: Application[] = yield call(Api.getApplications)
    yield put(listApplicationsSuccess(applications))
  } catch (error) {
    yield put(listApplicationsFail(error.message))
  }
}

export function* handleGetApartmentApplications({
  payload: { apartmentId }
}: any): SagaIterator {
  try {
    const selectedApartmentId: string | undefined = yield select(
      selectSelectedApartmentId
    )
    // if admin view, use selected apartmentId from state, otherwise use apartmentId from action payload
    const currentApartmentId = apartmentId || selectedApartmentId
    const applications: Application[] = yield call(
      Api.getApartmentApplications,
      currentApartmentId
    )
    yield put(getApartmentApplicationsSuccess({ apartmentId, applications }))
  } catch (error) {
    yield put(getApartmentApplicationsFail(error.message))
  }
}

function* accept(
  apartmentId: string,
  applicationId: string,
  confirmImmediately?: boolean
) {
  yield call(
    Api.acceptApplication,
    apartmentId,
    applicationId,
    confirmImmediately
  )
}

export function* acceptSelectedApplications(
  confirmImmediately?: boolean,
  passedApplicationIds?: string[]
) {
  const apartmentId: string = yield select(selectContentIdFromPathname)
  const applicationIds: string[] = yield select(selectSelectedApplications)
  if (passedApplicationIds)
    yield all(
      passedApplicationIds.map((id) =>
        accept(apartmentId, id, confirmImmediately)
      )
    )
  else if (applicationIds)
    yield all(
      applicationIds.map((id) => accept(apartmentId, id, confirmImmediately))
    )
  // TODO update store
}

export function* handleAcceptApplication({
  payload: { apartmentId, applicationId }
}: any): SagaIterator {
  try {
    const application: Application = yield call(
      Api.acceptApplication,
      apartmentId,
      applicationId
    )
    toast.info(i18n.t('applications.acceptSuccess'), { autoClose: 15000 })
    yield put(updateApplicationSuccess(application))
    yield put(acceptApplicationSuccess())
  } catch (error) {
    yield call(toast.error, i18n.t('error.application.acceptFail'))
    yield put(acceptApplicationFail(error.message))
  }
}

export function* handleRejectApplication({
  payload: { apartmentId, applicationId }
}: any): SagaIterator {
  try {
    const application: Application = yield call(
      Api.rejectApplication,
      apartmentId,
      applicationId
    )
    toast.info(i18n.t('applications.rejectSuccess'), { autoClose: 15000 })
    yield put(updateApplicationSuccess(application))
    yield put(rejectApplicationSuccess())
  } catch (error) {
    yield call(toast.error, i18n.t('error.application.rejectFail'))
    yield put(rejectApplicationFail(error.message))
  }
}

export function* handleCreateApplication({ payload }: any): SagaIterator {
  const actionPayload =
    payload && typeof payload === 'object' && payload !== null && 'formData' in payload
      ? payload
      : { formData: payload }
  const { formData, resolve, reject } = actionPayload
  try {
    const tempFormData = JSON.parse(JSON.stringify(formData))
    delete tempFormData.time
    delete tempFormData.dateRange
    delete tempFormData.phone

    if (formData.profilePicture) {
      const bucket = config.s3.profilePictures.BUCKET
      const region = config.s3.profilePictures.REGION
      const profilePictureUrl = yield call(
        uploadFileToS3,
        formData.profilePicture,
        bucket,
        region
      )
      tempFormData.profilePicture = profilePictureUrl.key
    }

    tempFormData.phone = formData.phone.phone
    const applicationId = formData.applicationId || ''

    const createApplication: ApplicationData = tempFormData
    const createdApplication = yield call(
      Api.createApplication,
      formData.apartmentCode,
      formData.language,
      '',
      createApplication,
      applicationId
    )
    if (formData.availabilities) {
      yield all(
        formData.availabilities.map((element: any, index: number) => {
          if (index === formData.availabilities.length - 1) {
            return null
          }
          const availability: AddApplicantAvailabilityFormData = {
            apartmentId: createdApplication.apartmentId,
            applicationId: createdApplication.applicationId,
            startDate: element.startDate,
            endDate: element.endDate,
            startTime: moment(element.timeRange.startTime).format(),
            endTime: moment(element.timeRange.endTime).format()
          }
          return call(
            ApplicantAvailabilityApi.createAvailabilities,
            availability as any
          )
        })
      )
    } else {
      const availability: AddApplicantAvailabilityFormData = {
        apartmentId: createdApplication.apartmentId,
        applicationId: createdApplication.applicationId,
        startDate: formData.dateRange.startDate,
        endDate: formData.dateRange.endDate,
        startTime: formData.time.startTime.format(),
        endTime: formData.time.endTime.format()
      }
      yield call(
        ApplicantAvailabilityApi.createAvailabilities,
        availability as any
      )
    }

    // SuperLike payment temporarily disabled
    // const shouldOpenPaymentPopup = Boolean(formData?.superLike)
    const shouldOpenPaymentPopup = false

    yield put(createApplicationSuccess(createdApplication))

    if (resolve) {
      yield call(resolve, {
        application: createdApplication,
        shouldOpenPaymentPopup
      })
    }

    if (shouldOpenPaymentPopup) {
      yield put(
        navigate(`/application/${createdApplication.code}/payment`)
      )
      return
    }

    yield put(navigate(`/aftershowing/${createdApplication.code}/steps/1?newApplication=true`))
  } catch (error) {
    const errorMessage =
      error?.response?.status === 409
        ? i18n.t('newApplication.errorDuplicate')
        : error.message
    yield put(createApplicationFail(errorMessage))
    if (reject) {
      yield call(
        reject,
        new SubmissionError({
          _error: errorMessage
        })
      )
    }
  }
}

export function* handleEditApplicationPhoneShown({
  payload: application
}: Action<Application>): SagaIterator {
  yield put(
    updateApplicationPhoneShownByCodeSuccess({
      ...application,
      phoneShown: true
    })
  )
  yield call(
    Api.updateApplicationPhoneShown,
    application.apartmentId,
    application.applicationId
  )
  // yield put(updateApplicationPhoneShownByCodeSuccess(updatedApplication))
}

export function* handleEditApplication({
  payload: { formData, resolve, reject }
}: any): SagaIterator {
  try {
    yield call(validateIsAdmin)
    const apartmentId: string = yield select(selectContentIdFromPathname)
    const applicationId: string = yield select(selectUserIdFromPathname)
    const editedApplication: CreateApplicationData = formData
    if (editedApplication.adminNotes) {
      delete editedApplication.adminNotes
    }
    /*if (editedApplication.apartmentCode) {
      delete editedApplication.apartmentCode
    }*/
    const updatedApplication = yield call(
      Api.editApplication,
      apartmentId,
      applicationId,
      editedApplication
    )
    yield put(updateApplicationSuccess(updatedApplication))
    yield put(editApplicationSuccess())
    yield call(getHistory().push, `/apartments` as any)
    resolve()
  } catch (error) {
    yield put(editApplicationFail(error.message))
    yield call(
      reject,
      new SubmissionError({ _error: i18n.t('apartment.actions.formFail') })
    )
  }
}

export function* handleUpdateApplicationAdminNotes({
  payload: { formData, resolve, reject, args }
}: any): SagaIterator {
  try {
    yield call(validateIsAdmin)
    // admin view uses selectedApartmentId, reservationsList from path
    const apartmentId: string = yield select(selectSelectedApartmentId)
    const { applicationId } = args
    const application = yield call(
      Api.updateApplicationAdminNotes,
      apartmentId,
      applicationId,
      formData
    )
    toast.info(i18n.t('apartment.adminInfo.success'), { autoClose: 15000 })
    yield put(updateApplicationSuccess(application))
    yield put(updateApplicationAdminNotesSuccess())
    resolve()
  } catch (error) {
    yield put(updateApplicationAdminNotesFail(error.message))
    yield call(
      reject,
      new SubmissionError({ _error: i18n.t('apartment.actions.formFail') })
    )
  }
}

export function* handleUpdateApplicationStatus({
  payload: { formData, resolve, reject }
}: any): SagaIterator {
  try {
    const { message, applicationId } = formData
    const apartmentId: string = yield select(selectSelectedApartmentId)
    const application = yield call(
      Api.updateApplicationStatus,
      apartmentId,
      applicationId,
      {
        message,
        status: 'manual'
      }
    )
    toast.info(i18n.t('adminApartments.applications.updateStatusSuccess'), {
      autoClose: 5000
    })
    yield put(updateApplicationStatusSuccess(application))
    resolve()
  } catch (error) {
    yield put(updateApplicationStatusFail(error.message))
    yield call(
      reject,
      new SubmissionError({ _error: i18n.t('apartment.actions.formFail') })
    )
  }
}

export function* handleSelectApplications(): SagaIterator {
  try {
    // const userId: string = yield select(selectUserIdFromPathname);
    // const apartmentId: string = yield select(selectContentIdFromPathname);
    // yield call(getHistory().push, {
    //   pathname: `/users/${userId}/apartments/${apartmentId}/showings`,
    // });
    yield put(selectApplicationsSuccess())
  } catch (error) {
    yield put(selectApplicationsFail(error.message))
  }
}

export function* handleUpdateApplicationStatusByCode({
  payload: { formData, resolve, reject }
}: FormPayloadParameters<UpdateStatusByCodeFormData>): SagaIterator {
  try {
    const code: string = yield select(selectApplicationCodeFromPathname)
    const stateReserveData: ReserveData | Record<string, never> = yield select(
      selectReserveData
    )
    const censoredApplication: CensoredApplication = yield call(
      Api.updateApplicationStatusByCode,
      code,
      {
        ...formData,
        showing:
          stateReserveData.reserved ||
          (Array.isArray(stateReserveData.invited) &&
            stateReserveData.invited.length > 0 &&
            stateReserveData.invited[0]) ||
          undefined
      }
    )

    const reserveData = {
      ...stateReserveData,
      application: censoredApplication
    }
    const dialogId =
      censoredApplication.status === 'noAvailableTimes'
        ? 'ConfirmNoAvailableTimes'
        : 'ConfirmNotInterested'

    // commented out cause showings get deleted now instead in the apis

    // if (dialogId === 'ConfirmNotInterested') {
    //   const showingIds = Array.isArray(stateReserveData.invited)
    //     ? stateReserveData.invited.map((showing: Showing) => showing.showingId)
    //     : []
    //   if (showingIds.length) {
    //     yield call(ShowingsApi.cancelShowing, {
    //       applicationCode: code,
    //       showingIds
    //     })
    //   }
    // }
    yield put(closeDialog(dialogId))
    yield call(getHistory().push, `/reserve/${code}/suggest` as any)
    yield put(updateApplicationStatusByCodeSuccess(reserveData))
    resolve()
  } catch (error) {
    yield call(
      reject,
      new SubmissionError({
        _error: i18n.t('error.application.updateStatusFail')
      })
    )
    yield put(updateApplicationStatusByCodeFail(error.message))
  }
}

export function* handleGetExportedLeadDetail({
  payload: leadId
}: {
  payload: string
}): SagaIterator {
  try {
    const leadData: PartnerLead = yield call(Api.getLeadData, leadId)
    yield put(getApartmentLeadSuccess({ leadId, leadData }))
  } catch (error) {
    yield put(getApartmentLeadFail(error.message))
  }
}

export function* handleGetConversationDetail({
  payload: applicationId
}: {
  payload: string
}): SagaIterator {
  try {
    const conversationData: any = yield call(
      Api.getConversationData,
      applicationId
    )
    yield put(getConversationSuccess({ applicationId, conversationData }))
  } catch (error) {
    yield put(getConversationFail(error.message))
  }
}

function* watchApplicationActions(): SagaIterator {
  yield all([
    takeLatest(getApplication, handleGetApplication),
    takeLatest(listApplications, handleListApplications),
    takeLatest(getApartmentApplications, handleGetApartmentApplications),
    takeLatest(getApartmentLead, handleGetExportedLeadDetail),
    takeLatest(getConversation, handleGetConversationDetail),
    takeLatest(acceptApplication, handleAcceptApplication),
    takeLatest(rejectApplication, handleRejectApplication),
    takeLatest(createApplication, handleCreateApplication),
    takeLatest(editApplication, handleEditApplication),
    takeLatest(updateApplicationAdminNotes, handleUpdateApplicationAdminNotes),
    takeLatest(
      updateApplicationPhoneShownByCode,
      handleEditApplicationPhoneShown
    ),
    takeLatest(
      updateApplicationStatusByCode,
      handleUpdateApplicationStatusByCode
    ),
    takeLatest(updateApplicationStatus, handleUpdateApplicationStatus),
    takeLatest(selectApplications, handleSelectApplications)
  ])
}

export default [watchApplicationActions]
